import { useCallback, useEffect, useRef, useState } from 'react'
import { eventTrackingService } from '../services/eventTrackingService'

// Minimal state for UI tracking - backend calculates metrics from events
interface QuestionState {
  questionId: string
  testQuestionId: string
  currentAnswer: string | null
  isMarkedForReview: boolean
}

interface UseEventTrackingOptions {
  attemptId: string
}

interface UseEventTrackingReturn {
  // Initialize tracking for a question
  openQuestion: (questionId: string, testQuestionId: string) => void
  // Close current question
  closeQuestion: () => void
  // Record answer selection
  selectAnswer: (option: string) => void
  // Record answer clear
  clearAnswer: () => void
  // Record mark for review
  markForReview: () => void
  // Record unmark from review
  unmarkForReview: () => void
  // Record scroll event (no-op, kept for API compatibility)
  recordScroll: () => void
  // Get current question state
  getCurrentQuestionState: () => QuestionState | null
  // Get state for a specific question
  getQuestionState: (questionId: string) => QuestionState | undefined
  // Check if initialized
  isInitialized: boolean
}

export function useEventTracking({
  attemptId,
}: UseEventTrackingOptions): UseEventTrackingReturn {
  const [isInitialized, setIsInitialized] = useState(false)
  const currentQuestionRef = useRef<QuestionState | null>(null)
  const questionStatesRef = useRef<Map<string, QuestionState>>(new Map())

  // Initialize the service
  useEffect(() => {
    if (attemptId) {
      eventTrackingService.initialize(attemptId)
      setIsInitialized(true)
    }

    return () => {
      // Close current question if any (synchronous, just queues event)
      if (currentQuestionRef.current && eventTrackingService.isInitialized()) {
        const state = currentQuestionRef.current
        eventTrackingService.trackQuestionClose(state.questionId, state.testQuestionId)
        currentQuestionRef.current = null
      }
      // Don't call async cleanup() here - it causes race conditions with React Strict Mode
      // The service is a singleton, re-initializing is fine
      // beforeunload/visibilitychange handlers will flush events when user leaves
      setIsInitialized(false)
    }
  }, [attemptId])

  // Internal function to close question
  const closeQuestionInternal = useCallback(() => {
    const state = currentQuestionRef.current
    if (!state) return

    // Send QUESTION_CLOSE event - service handles uninitialized state gracefully
    eventTrackingService.trackQuestionClose(state.questionId, state.testQuestionId)

    currentQuestionRef.current = null
  }, [])

  // Open a question
  const openQuestion = useCallback(
    (questionId: string, testQuestionId: string) => {
      // Close current question first if any
      if (currentQuestionRef.current) {
        closeQuestionInternal()
      }

      // Get existing state or create new
      const existingState = questionStatesRef.current.get(questionId)
      const newState: QuestionState = {
        questionId,
        testQuestionId,
        currentAnswer: existingState?.currentAnswer ?? null,
        isMarkedForReview: existingState?.isMarkedForReview ?? false,
      }

      currentQuestionRef.current = newState
      questionStatesRef.current.set(questionId, newState)

      // Send QUESTION_OPEN event - service handles uninitialized state gracefully
      eventTrackingService.trackQuestionOpen(questionId, testQuestionId)
    },
    [closeQuestionInternal]
  )

  // Close current question
  const closeQuestion = useCallback(() => {
    closeQuestionInternal()
  }, [closeQuestionInternal])

  // Select an answer
  const selectAnswer = useCallback((option: string) => {
    const state = currentQuestionRef.current
    if (!state) return

    // Don't send event if selecting same option
    if (state.currentAnswer === option) return

    // Update local state
    state.currentAnswer = option
    questionStatesRef.current.set(state.questionId, { ...state })

    // Send ANSWER_SELECT event - service handles uninitialized state gracefully
    eventTrackingService.trackAnswerSelect(
      state.questionId,
      state.testQuestionId,
      option
    )
  }, [])

  // Clear answer
  const clearAnswer = useCallback(() => {
    const state = currentQuestionRef.current
    if (!state || !state.currentAnswer) return

    // Update local state
    state.currentAnswer = null
    questionStatesRef.current.set(state.questionId, { ...state })

    // Send ANSWER_CLEAR event - service handles uninitialized state gracefully
    eventTrackingService.trackAnswerClear(state.questionId, state.testQuestionId)
  }, [])

  // Mark for review
  const markForReview = useCallback(() => {
    const state = currentQuestionRef.current
    if (!state) return

    state.isMarkedForReview = true
    questionStatesRef.current.set(state.questionId, { ...state })

    // Send MARK_FOR_REVIEW event - service handles uninitialized state gracefully
    eventTrackingService.trackMarkForReview(state.questionId, state.testQuestionId)
  }, [])

  // Unmark from review
  const unmarkForReview = useCallback(() => {
    const state = currentQuestionRef.current
    if (!state) return

    state.isMarkedForReview = false
    questionStatesRef.current.set(state.questionId, { ...state })

    // Send UNMARK_FOR_REVIEW event - service handles uninitialized state gracefully
    eventTrackingService.trackUnmarkForReview(state.questionId, state.testQuestionId)
  }, [])

  // Record scroll - kept for API compatibility, no-op since backend doesn't use it
  const recordScroll = useCallback(() => {
    // No-op - scroll tracking removed as it's not used for behavioral flags
  }, [])

  // Get current question state
  const getCurrentQuestionState = useCallback(() => {
    return currentQuestionRef.current
  }, [])

  // Get state for a specific question
  const getQuestionState = useCallback((questionId: string) => {
    return questionStatesRef.current.get(questionId)
  }, [])

  return {
    openQuestion,
    closeQuestion,
    selectAnswer,
    clearAnswer,
    markForReview,
    unmarkForReview,
    recordScroll,
    getCurrentQuestionState,
    getQuestionState,
    isInitialized,
  }
}

export default useEventTracking
